import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { subHours, getUnixTime } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { Tab, Tabs } from '@material-ui/core';
import classNames from 'classnames';
import _ from 'lodash';

import AppContext from '../../../App.context';
import { RootState } from 'src/configs/store';
import BetHistory from 'src/components/Account/AccountHistory/BetHistory/BetHistory';
import Button from 'src/components/Button/Button';
import GoUpButton from 'src/components/GoUpButton/GoUpButton';

export default function MyBets() {
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [betHistory, setBetHistory] = useState([]);
  const [lastPage, setLastPage] = useState(false);

  const { authenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const { isPortrait } = useContext(AppContext);
  const history = useHistory();
  const { t } = useTranslation();
  const refElement = useRef();

  const tabs = ['openBets', 'settled'];

  const loadBets = async (concatHistory: boolean = true) => {
    const data = await dispatch.account.getBetHistory({
      page: activeTab === 0 ? page : -1,
      fromDate: activeTab === 0 ? undefined : getUnixTime(subHours(new Date(), 24)),
      type: activeTab === 0 ? 'openBets' : 'settled'
    });
    setBetHistory((concatHistory ? _.concat(betHistory, data.results.collection) : data.results.collection) || []);

    const isLastPage = data.lastPage || data.results.collection.length < 10 ? true : false ;
    setLastPage(isLastPage);
  };

  const clearBets = () => {
    setBetHistory([]);
    setLastPage(false);
  };

  const handleOpenBetHistory = () => {
    if ((isMobileOnly || (isTablet && isPortrait))) {
      dispatch.content.openMenu('accountHistory');
    } else {
      history.push('/account/betting-history');
    }
  };

  useEffect(() => {
    authenticated ? loadBets() : clearBets();
  }, [page, authenticated]);

  useEffect(() => {
    clearBets();
    loadBets(false);
  }, [activeTab]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      loadBets();
    }, 120000);

    return () => {
      clearInterval(refreshInterval);
    };
  });

  return (
    <>
      <div className="my-bets__tabs">
        <Tabs
          value={ activeTab }
          centered={ true }
          TabIndicatorProps={ { className: 'my-bets__tabs-container' } }
          variant="fullWidth"
        >
          {
            _.map(tabs, (tab, index) => {
              return (
                <Tab
                  label={ t(`betHistory.${ tab }`) }
                  onClick={ () => setActiveTab(index) }
                  key={ tab }
                  className={
                    classNames({
                      'my-bets__tab--selected' : activeTab === index,
                      'my-bets__tab': true
                    })
                  }
                />);
            })
          }
        </Tabs>
      </div>
      <div className="betting-history my-bets" ref={ refElement }>
        <div className="betting-history__list">
          { !authenticated && <span className="betting-history__log-in">{ t('betHistory.pleaseLogIn') }</span> }
          {
            authenticated && betHistory?.length === 0 && activeTab === 0 && (
              <span className="betting-history__log-in">{ t('betHistory.noOpenBets') }</span>
            )
          }
          {
            _.map(betHistory, item => (
              <BetHistory
                key={ item.ticketNumber }
                item={ item }
                myBets
                activeTab={ tabs[activeTab] }
              />
            ))
          }
        </div>
        {
          authenticated && activeTab === 1 && (
            <div className="betting-history__settled-bets-message">
              <Trans i18nKey="betHistory.settledBetsAccountHistory">
                <span onClick={ handleOpenBetHistory } />
              </Trans>
            </div>
          )
        }
        {
          betHistory?.length !== 0 && !lastPage && (
            <Button
              className="btn--primary betting-history__load-more"
              onClick={ () => { setPage(page+1); } }
            >
              { t('betHistory.loadMore') }
            </Button>
          )
        }
        { refElement && <GoUpButton refElement={ refElement } showAfterPage={ 1 } /> }
      </div>
    </>
  );
}
